import React, { useState } from "react";
import { Plus, Trash2, ChevronDown, ChevronRight, GripVertical } from "lucide-react";
import MultiImageUpload from "@/components/MultiImageUpload";

/**
 * AlbumsEditor — edita uma lista de sub-álbuns.
 * value: [{ id, name, images: string[] }, ...]
 */
function makeAlbum(name = "") {
  return {
    id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `a-${Date.now()}-${Math.random()}`,
    name,
    images: [],
  };
}

export default function AlbumsEditor({ value = [], onChange, testId }) {
  const list = Array.isArray(value) ? value : [];
  const [openIdx, setOpenIdx] = useState(list.length ? 0 : -1);
  const [dragIdx, setDragIdx] = useState(null);

  const patch = (idx, changes) => {
    const next = list.slice();
    next[idx] = { ...next[idx], ...changes };
    onChange(next);
  };

  const add = () => {
    const next = [...list, makeAlbum(`Álbum ${list.length + 1}`)];
    onChange(next);
    setOpenIdx(next.length - 1);
  };

  const remove = (idx) => {
    if (!window.confirm(`Excluir álbum "${list[idx].name || `Álbum ${idx + 1}`}"?`)) return;
    const next = list.slice();
    next.splice(idx, 1);
    onChange(next);
    setOpenIdx(-1);
  };

  const move = (from, to) => {
    if (from === to || to < 0 || to >= list.length) return;
    const next = list.slice();
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    onChange(next);
    setOpenIdx(to);
  };

  return (
    <div className="space-y-2" data-testid={testId ? `${testId}-wrapper` : undefined}>
      {list.length === 0 && (
        <div className="text-xs text-white/60 italic px-3 py-4 border border-dashed border-white/10">
          Nenhum álbum ainda. Clique em "Adicionar álbum" para começar.
        </div>
      )}
      {list.map((alb, idx) => {
        const open = openIdx === idx;
        return (
          <div
            key={alb.id || idx}
            draggable
            onDragStart={() => setDragIdx(idx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (dragIdx !== null) move(dragIdx, idx);
              setDragIdx(null);
            }}
            className="border border-white/10 bg-black/30"
            data-testid={testId ? `${testId}-album-${idx}` : undefined}
          >
            <div className="flex items-center px-3 py-2 gap-2">
              <span className="cursor-grab text-white/40" title="Arraste para reordenar"><GripVertical size={14} /></span>
              <button
                type="button"
                onClick={() => setOpenIdx(open ? -1 : idx)}
                className="text-white/80 hover:text-[color:var(--marupiara-gold)] flex items-center gap-1 flex-1 text-left"
                data-testid={testId ? `${testId}-toggle-${idx}` : undefined}
              >
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <input
                  value={alb.name || ""}
                  onChange={(e) => patch(idx, { name: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  placeholder={`Nome do álbum ${idx + 1}`}
                  className="flex-1 bg-transparent text-white text-sm focus:outline-none border-b border-transparent focus:border-[color:var(--marupiara-gold)]/40 px-1"
                  data-testid={testId ? `${testId}-name-${idx}` : undefined}
                />
                <span className="text-[10px] text-white/50 whitespace-nowrap">{(alb.images || []).length} foto(s)</span>
              </button>
              <button
                type="button"
                onClick={() => remove(idx)}
                data-testid={testId ? `${testId}-remove-${idx}` : undefined}
                className="text-red-300 hover:text-red-200 p-1"
                title="Remover álbum"
              >
                <Trash2 size={14} />
              </button>
            </div>
            {open && (
              <div className="border-t border-white/10 p-3">
                <MultiImageUpload
                  value={alb.images || []}
                  onChange={(imgs) => patch(idx, { images: imgs })}
                  testId={testId ? `${testId}-images-${idx}` : undefined}
                />
              </div>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={add}
        data-testid={testId ? `${testId}-add` : undefined}
        className="text-xs px-3 py-2 border border-[color:var(--marupiara-gold)]/40 text-[color:var(--marupiara-gold)] hover:bg-[color:var(--marupiara-gold)]/10 flex items-center gap-1"
      >
        <Plus size={12} /> Adicionar álbum
      </button>
    </div>
  );
}
